import { Search } from '@mui/icons-material'
import { Box, MenuItem, OutlinedInput, Select, Theme, Typography } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { useEffect, useState } from 'react'
import { LogLevel } from '../../../api/DTO'
import { MultiSelect } from '../../selects/MultiSelect'
import { FilterControl, MultiSelectFilter, SelectFilter, SelectFilterOption, SortBy, TableDisplay } from './DTO'
import { DateFilter } from './DateFilter'

type TableFiltersProps = {
	filters: FilterControl[]
	onChange: (name: string, value: unknown) => void
}

const TableFilters = (
	props: TableFiltersProps,
) => {
	const [filters, setFilters] = useState<FilterControl[]>(props.filters)

	useEffect(() => setFilters(props.filters), [props.filters])

	const onChange = (name: string, value: unknown) => {
		setFilters(filters.map((filter) => filter.name !== name ? filter
			: { ...filter, value: value } as FilterControl))
		props.onChange(name, value)
	}

	return (
		<Box
			display="flex"
			alignItems="center"
			width="100%"
		>
			<Box display="flex" gap={3} alignItems="center">
				{filters.filter((filter) => !filter.isDisplaySetting).map((filter) => <TableFilter key={filter.name} filter={filter} onChange={onChange} />)}
			</Box>
			<Box display="flex" gap={3} alignItems="center">
				{filters.filter((filter) => filter.isDisplaySetting).map((filter) => <TableFilter key={filter.name} filter={filter} onChange={onChange} />)}
			</Box>
		</Box>
	)
}

export default TableFilters

type TableFilterProps = {
	filter: FilterControl
	onChange: (name: string, value: unknown) => void
}

const TableFilter = (props: TableFilterProps) => {
	const styles = useStyles()

	const filter = props.filter
	switch (filter.type) {
		case "date range":
			return <DateFilter
				dateText={filter.label}
				dateFrom={filter.value?.start}
				dateTo={filter.value?.end}
				onChange={(dateFrom, dateTo) =>
					props.onChange(
						filter.name,
						dateFrom && dateTo ? { start: dateFrom, end: dateTo } : undefined
					)
				}
			/>
		case "search":
			return <OutlinedInput
				size="small"
				className={styles.searchBox}
				value={filter.value ?? ''}
				placeholder={filter.placeHolder ?? "Search"}
				endAdornment={<Search />}
				onChange={(evt) => {
					props.onChange(
						filter.name,
						evt.target.value === '' ? undefined : evt.target.value,
					)
				}}
			/>
		case "multiselect":
			return <MultiSelectFilterComponent {...filter} onChange={props.onChange} />
		case "log level":
			return <MultiSelectFilterComponent {...filter} onChange={props.onChange} options={logLevelOptions} />
		case "select":
			return <SelectFilterComponent {...filter} onChange={props.onChange} />
		case "sort by age":
			return <SelectFilterComponent {...filter} onChange={props.onChange} options={sortByAgeOptions} />
		case "table display":
			return <SelectFilterComponent {...filter} onChange={props.onChange} options={tableDisplayOptions} />
		//case "custom":
		default:
			return null
	}
}

type MultiSelectFilterComponentProps = MultiSelectFilter & {
	onChange: (name: string, value: unknown) => void
}

const MultiSelectFilterComponent = ({ name, label, value, options, onChange }: MultiSelectFilterComponentProps) =>
	<MultiSelect
		label={label ?? name}
		options={options ?? []}
		value={value ?? []}
		onChange={(values) =>
			onChange(
				name,
				values.length === 0 ? undefined : values
			)
		}
	/>

type SelectFilterComponentProps = SelectFilter & {
	onChange: (name: string, value: unknown) => void
}

const SelectFilterComponent = ({ name, value, options = [], onChange }: SelectFilterComponentProps) =>
	<Select
		size="small"
		value={value ?? options[0].value}
		style={{ minWidth: 80 }}
		onChange={(evt) => {
			onChange(
				name,
				evt.target.value,
			)
		}}
		renderValue={(value) => {
			const option = options.find((option: SelectFilterOption) => option.value === value)
			return option?.label ?? option?.value
		}}
	>
		{options.map((option: SelectFilterOption) =>
			<MenuItem key={option.value} value={option.value}>
				<Typography variant="body2">{option.label ?? option.value}</Typography>
			</MenuItem>
		)}
	</Select>

const useStyles = makeStyles((theme: Theme) => ({
	searchBox: {
		height: 40,
		marginLeft: theme.spacing(1),
		marginRight: theme.spacing(1),
	},
}))

const enumToOptionArray = (en: Record<string, string | number>) =>
	Object.keys(en)
		.filter(key => isNaN(parseInt(key)))
		.map((key) => ({
			label: key,
			value: en[key]
		}))

const logLevelOptions = enumToOptionArray(LogLevel)
const sortByAgeOptions = enumToOptionArray(SortBy)
const tableDisplayOptions = enumToOptionArray(TableDisplay)